import { createClient, type SupabaseClient } from '@supabase/supabase-js';

/**
 * Browser Supabase client (anon key — RLS enforced).
 *
 * This is the V3 auth surface (`security.mdc` §3): Supabase Auth owns
 * password login, recovery, refresh, and MFA. The anon key is PUBLIC by
 * construction (`fe-performance.mdc` §9 / `security.mdc` §2) — it carries no
 * privilege beyond what RLS allows, so it is safe to ship in the bundle.
 *
 * The client is created lazily and only when both `VITE_SUPABASE_URL` and
 * `VITE_SUPABASE_ANON_KEY` are configured. When they are absent (the default
 * local-dev posture, which still uses the legacy Express auth bridge), the
 * Supabase auth surface is simply inert — callers gate on
 * {@link isSupabaseAuthEnabled} first.
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
const SUPABASE_AUTH_FLAG = import.meta.env.VITE_SUPABASE_AUTH as string | undefined;

let client: SupabaseClient | null = null;

/** Whether the Supabase auth surface is configured for this build. */
export function isSupabaseAuthEnabled(): boolean {
  return Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);
}

/**
 * Whether Supabase Auth is the ACTIVE primary login path (the interim cutover
 * flag). Requires the client to be configured AND `VITE_SUPABASE_AUTH=true`.
 * When false, the legacy Express login remains the primary path — flipping this
 * env flag is the instant rollback. Distinct from {@link isSupabaseAuthEnabled}
 * (mere configuration) so the password-reset building blocks can be present
 * without yet rerouting the primary login.
 */
export function isSupabaseLoginEnabled(): boolean {
  return isSupabaseAuthEnabled() && String(SUPABASE_AUTH_FLAG).toLowerCase() === 'true';
}

/**
 * Return the singleton browser Supabase client.
 *
 * @throws if the Supabase env vars are not configured — callers must gate on
 *   {@link isSupabaseAuthEnabled} so this never throws in the legacy posture.
 */
export function getSupabaseClient(): SupabaseClient {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    throw new Error(
      'Supabase auth is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.',
    );
  }
  if (!client) {
    client = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        // Recovery / magic links arrive in the URL hash; let the client
        // consume them on load so the set-password screen has a session.
        detectSessionInUrl: true,
        persistSession: true,
        autoRefreshToken: true,
        flowType: 'pkce',
      },
    });
  }
  return client;
}
